import type { BasketItem, Product, ProductSize } from '../types/order';
import { defaultSize, sizeChannelPrice, sortSizes } from './pricing';

/**
 * FE-4: επαναφορά προχείρου take away πάνω στον ΤΡΕΧΟΝΤΑ κατάλογο.
 *
 * Το πρόχειρο (order_drafts.draft_data) κρατά ολόκληρα στιγμιότυπα προϊόντων και μεγεθών
 * από τη στιγμή της στάθμευσης. Όταν στο μεταξύ τα μεγέθη ξαναφτιαχτούν (μετάπτωση
 * «τιμή μόνο στο μέγεθος», συγχρονισμός οργανισμού, διαχωρισμός servers), το στιγμιότυπο
 * δείχνει σε id μεγέθους που δεν υπάρχει πια — και ο server θα έγραφε λάθος ή κανένα
 * internal_size_id (συνταγή, απόθεμα, εκτύπωση). Εδώ ξαναδένουμε κάθε γραμμή με το
 * προϊόν/μέγεθος του καταλόγου ΚΑΤΑ ID.
 *
 * ΤΟ totalPrice ΔΕΝ ΑΛΛΑΖΕΙ ΠΟΤΕ (σχέδιο §5.3: τα πρόχειρα κρατούν τα ποσά τους). Για τον
 * ίδιο λόγο κρατάμε από το στιγμιότυπο και όσα πεδία του προϊόντος καθορίζουν ποσό μετά
 * την επαναφορά (FROZEN_PRODUCT_FIELDS): ΦΠΑ της γραμμής στην απόδειξη, κατηγορία τέλους
 * και πλήθος αυτόματων τελών. Αλλιώς μια αλλαγή καταλόγου ανάμεσα σε στάθμευση και
 * επαναφορά θα άλλαζε σιωπηλά το ΦΠΑ ή την ποσότητα τέλους ενός ήδη χτισμένου καλαθιού.
 */

// Πεδία του προϊόντος που μένουν όπως στάθμευσαν. vat → vatRate της απόδειξης
// (TakeAway generateCashReceipt· το fiscalDocumentService το προτιμά από τη βάση)·
// fee_category → ταξινόμηση γραμμής ως τέλος (isStateFee, carve εκπτώσεων)·
// auto_plastic_fee_count → πόσα αυτόματα τέλη 16 ζητά το effect του TakeAway.
const FROZEN_PRODUCT_FIELDS = [
  'vat', 'vat_take_away', 'vat_delivery', 'vat_dine_in', 'fee_category', 'auto_plastic_fee_count',
] as const;

// Το πρόθεμα με το οποίο το TakeAway σημαδεύει τη γραμμή τέλους που έβαλε ΜΟΝΟ του.
const AUTO_FEE_16_PREFIX = 'auto-fee-16-';

export interface RehydrateResult {
  basket: BasketItem[];
  /** Ονόματα γραμμών που ΔΕΝ ξαναδέθηκαν: «προϊόν» ή «προϊόν (μέγεθος)». */
  unresolved: string[];
}

const sameId = (a: unknown, b: unknown) => a != null && b != null && Number(a) === Number(b);
const toCents = (n: number) => Math.round(Number(n) * 100);

/** Προϊόν καταλόγου με τα «παγωμένα» πεδία του στιγμιοτύπου. */
function withFrozenFields(cat: Product, snapshot: Product): Product {
  const out: Product = { ...cat };
  for (const k of FROZEN_PRODUCT_FIELDS) {
    // Και το undefined αντιγράφεται: πρόχειρο από παλιότερο ταμείο χωρίς το πεδίο
    // συμπεριφερόταν ως «χωρίς» — έτσι μένει.
    (out as any)[k] = (snapshot as any)?.[k];
  }
  return out;
}

// Όνομα μεγέθους χωρίς τόνους/πεζά-κεφαλαία: ένα μέγεθος που ξαναφτιάχτηκε από κεντρικό
// sync ως «ΚΑΝΟΝΙΚΟ» αντί «Κανονικό» είναι το ίδιο μέγεθος (παγίδα τόνων του CLAUDE.md).
const normSizeName = (s: unknown) =>
  String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLocaleUpperCase('el');

/**
 * Μέγεθος του καταλόγου κατά id, αλλιώς κατά όνομα (ξαναφτιαγμένο μέγεθος). Κατά όνομα
 * ΜΟΝΟ αν ταιριάζει ακριβώς ένα: με δύο υποψήφια δεν ξέρουμε ποιο χρεώθηκε → null, και η
 * γραμμή μένει ολόκληρη στιγμιότυπο.
 */
function findCatalogSize(cat: Product, snap: ProductSize): ProductSize | null {
  const sizes = sortSizes(cat.sizes || []);
  const byId = sizes.find(s => sameId(s.id, snap.id));
  if (byId) return byId;
  const name = normSizeName(snap.name);
  if (!name) return null;
  const byName = sizes.filter(s => normSizeName(s.name) === name);
  return byName.length === 1 ? byName[0] : null;
}

/**
 * Γραμμή χωρίς μέγεθος (όχι τέλος) που είναι «σκέτη» — τίποτα πέρα από την τιμή του
 * προεπιλεγμένου μεγέθους. Μόνο τότε ξέρουμε με βεβαιότητα ποιο μέγεθος χρεώθηκε.
 */
function isPlainLine(item: BasketItem, cat: Product): boolean {
  if (item.weight != null || cat.is_weight_variable || item.product?.is_weight_variable) return false;
  if (cat.is_open_price || item.product?.is_open_price) return false;
  if (cat.fee_category != null || item.product?.fee_category != null) return false;
  if ((item.addedExtras?.length ?? 0) > 0) return false;
  if ((item.selected_options?.length ?? 0) > 0) return false;
  if ((item.removedIngredients?.length ?? 0) > 0) return false;
  if (item.discount) return false;
  if (item.applied_promotion_id != null || item.original_unit_price != null) return false;
  return true;
}

/**
 * Ξαναδένει τις γραμμές του προχείρου με τον κατάλογο. Άδειος κατάλογος (δεν έχει φορτώσει)
 * → το καλάθι αυτούσιο, όπως πριν το FE-4. Δεν αλλάζει τον πίνακα εισόδου.
 */
export function rehydrateDraftBasket(basket: BasketItem[], catalog: Product[] | null | undefined): RehydrateResult {
  if (!Array.isArray(basket) || !catalog || catalog.length === 0) {
    return { basket, unresolved: [] };
  }

  const unresolved: string[] = [];
  // Το ίδιο προϊόν-τέλος που διαλέγει το effect του αυτόματου τέλους στο TakeAway
  // (products.find(fee_category === 16))· αλλιώς εκείνο θα έβαζε δεύτερη γραμμή τέλους.
  const currentFee16 = catalog.find(p => p.fee_category === 16) ?? null;

  const out = basket.map(item => {
    const snapProduct = item.product;
    const isAutoFee = String(item.id ?? '').startsWith(AUTO_FEE_16_PREFIX);

    let cat: Product | undefined;
    if (isAutoFee && currentFee16) cat = currentFee16;
    else cat = catalog.find(p => sameId(p.id, snapProduct?.id));

    if (!cat) {
      unresolved.push(String(snapProduct?.name ?? '?'));
      return item;
    }

    // Το μέγεθος αποφασίζεται ΠΡΙΝ χτιστεί η νέα γραμμή: αν δεν βρεθεί στον κατάλογο, η
    // γραμμή μένει ΟΛΟΚΛΗΡΗ στιγμιότυπο (προϊόν ΚΑΙ μέγεθος), όπως όταν λείπει το προϊόν.
    // Μισή-μισή (προϊόν καταλόγου + μέγεθος στιγμιοτύπου) θα ξαναϋπολογιζόταν λάθος στην
    // επεξεργασία (updateBasketItem: τιμή του μεγέθους) — π.χ. η παλιά προσαύξηση ενός
    // μεγέθους πριν τη μετάπτωση θα χρεωνόταν ως απόλυτη τιμή.
    let catSize: ProductSize | null = null;
    if (item.selectedSize) {
      catSize = findCatalogSize(cat, item.selectedSize);
      if (!catSize) {
        unresolved.push(`${cat.name} (${item.selectedSize.name})`);
        return item;
      }
    }

    const next: BasketItem = { ...item, product: withFrozenFields(cat, snapProduct) };

    if (catSize) {
      next.selectedSize = catSize;
    } else if (!isAutoFee && isPlainLine(item, cat)) {
      // Χωρίς μέγεθος: ο server θα τιμολογούσε το defaultSize (απόφαση 12). Το δένουμε ρητά
      // ΜΟΝΟ αν η τιμή του ταιριάζει ακριβώς (σε λεπτά) με το totalPrice της γραμμής — αλλιώς
      // δεν ξέρουμε τι χρεώθηκε και η γραμμή μένει χωρίς μέγεθος. Τα τέλη τα αφήνει έξω το
      // isPlainLine (ποτέ μέγεθος).
      const def = defaultSize(cat.sizes);
      next.selectedSize = def && toCents(sizeChannelPrice(def, 'take_away')) === toCents(item.totalPrice)
        ? def
        : null;
    }

    // totalPrice/quantity/έξτρα/σημειώσεις: από το πρόχειρο, ανέγγιχτα (§5.3).
    next.totalPrice = item.totalPrice;
    return next;
  });

  return { basket: out, unresolved };
}
